import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, BadgeCheck, Repeat, Sprout, Gem } from "lucide-react";
import { fetchFeatured, fetchNiches, fetchStats } from "@/lib/api";
import { ProgramCard } from "@/components/ProgramCard";
import { NewsletterSection } from "@/components/NewsletterSection";
import { GatewaySection } from "@/components/GatewaySection";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { WhySection } from "@/components/WhySection";
import { LeaderboardWidget } from "@/components/LeaderboardWidget";
import * as Icons from "lucide-react";

const NicheIcon = ({ name, className }) => {
  const Icon = Icons[name] || Icons.Folder;
  return <Icon className={className} />;
};

export default function HomePage() {
  const [featured, setFeatured] = useState(null);
  const [niches, setNiches] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeatured().then(setFeatured).catch(console.error);
    fetchNiches().then((d) => setNiches(d.niches)).catch(console.error);
    fetchStats().then(setStats).catch(console.error);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/programs${query ? `?search=${encodeURIComponent(query)}` : ""}`);
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative bg-slate-900 overflow-hidden">
        <div className="noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-24 md:py-32">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-400 mb-6">Free to join · Earn up to 21% commission · Ready-made ads included</p>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tight font-medium text-white max-w-3xl leading-[1.05]">
            Turn your audience into <span className="text-emerald-400">income</span>. Starting today.
          </h1>
          <p className="text-slate-400 text-base md:text-lg mt-6 max-w-xl leading-relaxed">
            Social Flats is your exclusive gateway to the affiliate network. Compare 55+ vetted programs, grab ready-made ads, and get paid per click — free to join, no catch.
          </p>
          <form onSubmit={onSearch} className="mt-10 flex max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Shopify, HubSpot, hosting…"
                data-testid="hero-search-input"
                className="w-full pl-14 pr-4 py-4 rounded-l-full bg-white/10 border border-white/20 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <button type="submit" data-testid="hero-search-button" className="px-8 py-4 rounded-r-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200">
              Search
            </button>
          </form>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl">
            <Link to="/signup" data-testid="hero-join-cta" className="flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-white text-slate-900 text-sm font-semibold hover:bg-slate-200 transition-colors duration-200">
              Join Free — Earn 15% Per Click
            </Link>
            <Link to="/rewards" data-testid="hero-rewards-cta" className="flex items-center justify-center gap-2 px-7 py-3 rounded-full border border-white/25 text-white text-sm font-medium hover:bg-white/10 transition-colors duration-200">
              View Rewards & Tiers <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {stats && (
            <div data-testid="hero-stats" className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-2xl">
              {[
                [stats.total_programs, "Programs tracked"],
                [stats.niches, "Niches covered"],
                [stats.beginner_friendly, "Beginner friendly"],
                [stats.recurring, "Recurring payouts"],
              ].map(([v, l]) => (
                <div key={l}>
                  <p className="font-heading text-3xl text-white font-medium">{v}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{l}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <GatewaySection />

      {/* Editor's Choice */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-amber-500" /> Editor's Choice</p>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight">Top picks this month</h2>
          </div>
          <Link to="/programs" data-testid="view-all-programs-link" className="hidden sm:flex items-center gap-1 text-sm font-medium text-emerald-700 hover:gap-2 transition-[gap] duration-200">
            View all programs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(featured?.editors_choice || []).slice(0, 4).map((p) => <ProgramCard key={p.slug} program={p} />)}
        </div>
      </section>

      {/* Niches */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-24">
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-2">Niche Explorer</p>
        <h2 className="font-heading text-3xl md:text-4xl tracking-tight mb-8">Browse by niche</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {niches.map((n) => (
            <Link
              key={n.slug}
              to={`/niches/${n.slug}`}
              data-testid={`niche-card-${n.slug}`}
              className="card-lift bg-white border border-slate-200 rounded-lg p-5 group"
            >
              <NicheIcon name={n.icon} className="w-6 h-6 text-emerald-600 mb-3" />
              <p className="font-heading font-medium text-sm leading-snug group-hover:text-emerald-700 transition-colors duration-200">{n.name}</p>
              <p className="text-xs text-slate-500 mt-1">{n.program_count} programs · ${n.avg_epc} avg EPC</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Trending rows */}
      {featured && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-24 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {[
            { icon: Repeat, title: "Highest Recurring", items: featured.best_recurring, tid: "recurring" },
            { icon: Sprout, title: "Best for Beginners", items: featured.best_for_beginners, tid: "beginners" },
            { icon: Gem, title: "High-Ticket Offers", items: featured.high_ticket, tid: "high-ticket" },
          ].map(({ icon: Icon, title, items, tid }) => (
            <div key={title} data-testid={`trending-${tid}-list`} className="bg-white border border-slate-200 rounded-lg p-6">
              <p className="flex items-center gap-2 font-heading font-semibold text-lg mb-4"><Icon className="w-5 h-5 text-emerald-600" /> {title}</p>
              <ul className="divide-y divide-slate-100">
                {items.slice(0, 5).map((p) => (
                  <li key={p.slug}>
                    <Link to={`/programs/${p.slug}`} className="flex items-center justify-between py-3 group">
                      <span className="text-sm font-medium group-hover:text-emerald-700 transition-colors duration-200">{p.name}</span>
                      <span className="text-xs text-slate-500">{p.commission}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      <TestimonialsSection />

      <LeaderboardWidget />

      <WhySection />

      <NewsletterSection />
    </main>
  );
}
